import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiRadio from '@mui/material/Radio';
import {Field} from '@practicegenius/react-final-form/src/Field';

type RadioProps = {
  value: string | number;
  color?: 'primary' | 'secondary' | 'default';
  label: string | JSX.Element;
  labelProps?: {style: React.ComponentProps<typeof Typography>['style']};
  name: string;
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
};

export const Radio: React.FC<RadioProps> = ({
  color = 'primary',
  label,
  labelProps = {style: {fontWeight: 600}},
  name,
  ...props
}) => {
  const id = `${name}_${props.value}`;
  return (
    <Box display="flex" alignItems="center">
      <MuiRadio id={id} name={name} color={color} {...props} />
      <Typography
        variant="subtitle1"
        style={labelProps.style}
        component="label"
        htmlFor={id}
      >
        {label}
      </Typography>
    </Box>
  );
};

type RadioGroupProps = {
  options: Omit<RadioProps, 'onChange' | 'name' | 'checked'>[];
  value: React.ReactNode;
  name: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
};

export const RadioGroup: React.FC<RadioGroupProps> = ({
  options,
  value,
  onChange,
  name,
}) => (
  <Box mt={0.25} mb={0.5}>
    {options.map(option => (
      <Box key={option.value} mt={-0.125} mb={-0.875}>
        <Radio
          name={name}
          {...option}
          checked={value === option.value}
          onChange={onChange}
        />
      </Box>
    ))}
  </Box>
);

export const RadioGroupField: React.FC<RadioGroupProps> = ({
  name,
  value,
  options,
}) => (
  <Field name={name} type="radio" value={value}>
    {({input}): React.ReactNode => <RadioGroup {...input} options={options} />}
  </Field>
);
